import { List } from "lodash"
import difference from "lodash/difference"
import get from "lodash/get"
import { useEffect, useState } from "react"
import {
  useCohortSyncPlanContext,
  useCohortSyncPlanQuery
} from "./SyncPlanContext"
import { SYNC_DIR_BACKWARD, SYNC_DIR_FORWARD } from "./constants"
import { Change, SelectedSourceIDs, SyncDirection } from "./types"

// helper hook for handling the syncDir
export const useSyncDir = ({
  syncDir: syncDirProp,
  cohortId
}: {
  syncDir: SyncDirection
  cohortId: number
}): [SyncDirection, (syncDir: SyncDirection) => void] => {
  const [syncDir, setSyncDir] = useState<SyncDirection>(syncDirProp)

  // update syncDir state from prop
  useEffect(() => {
    if (syncDirProp !== syncDir) setSyncDir(syncDirProp)
  }, [syncDirProp])

  const { fromCohort, toCohort } = useCohortSyncPlanQuery({
    cohortId
  })

  // ensure syncDir state is accurately set
  useEffect(() => {
    // prevent SYNC_DIR_BACKWARD if there is no toCohort
    if (fromCohort && !toCohort && syncDir !== SYNC_DIR_FORWARD)
      setSyncDir(SYNC_DIR_FORWARD)
  }, [fromCohort, toCohort, syncDir])

  return [syncDir, setSyncDir]
}

// tracking the current user-included/-excluded changes
// handles requerying the sync plan based on these changes
// stays in sync with the sync plan included/excluded changes
export const useSelectedItemSourceIds = ({
  cohortId,
  syncDir,
  defaultSelectedItemSourceIDs,
  allowBackSync
}: {
  cohortId: number
  syncDir: SyncDirection
  defaultSelectedItemSourceIDs?: SelectedSourceIDs
  allowBackSync?: boolean
}): [
  SelectedSourceIDs,
  (itemSourceId: number) => void,
  (selectedItemSourceIds: SelectedSourceIDs) => void
] => {
  // NOTE: initializing changes as null to ensure we do not initially exclude all changes
  // from the syncPlan query!
  const getDefaultSelectedItemSourceIds = (): SelectedSourceIDs =>
    ({
      [SYNC_DIR_FORWARD]: null,
      [SYNC_DIR_BACKWARD]: null
    } as SelectedSourceIDs)

  const [selectedItemSourceIds, setSelectedItemSourceIds] =
    useState<SelectedSourceIDs>(
      defaultSelectedItemSourceIDs || getDefaultSelectedItemSourceIds
    )

  const { [syncDir]: syncPlan } = useCohortSyncPlanContext({
    cohortId,
    selectedItemSourceIds,
    allowBackSync
  })

  // keep state in sync with the syncPlan
  useEffect(() => {
    if (syncPlan && !syncPlan.query?.loading) {
      // itemSourceIds for current syncDir
      const itemSourceIds = selectedItemSourceIds[
        syncDir
      ] as SelectedSourceIDs[SyncDirection]

      // get includedSourceIds from sync plan
      const includedSourceIds =
        syncPlan.includedChanges?.map(
          (change: Change) => change.item_source_id
        ) || []

      // ensure we only update state if included/exluded source ids have changed
      if (
        difference(includedSourceIds, itemSourceIds as List<number>).length ||
        !itemSourceIds
      ) {
        // update state
        setSelectedItemSourceIds({
          ...selectedItemSourceIds,
          [syncDir]: includedSourceIds
        })
      }
    }
  }, [get(syncPlan, "query.data")])

  // toggles the itemSourceId into/out of the selectedItemSourceIds
  const toggleSelectedItemSourceId = (itemSourceId: number) => {
    let itemSourceIds = selectedItemSourceIds[syncDir] || []

    if (itemSourceIds?.includes(itemSourceId)) {
      // already included, filter it out
      itemSourceIds = itemSourceIds.filter((id) => id !== itemSourceId)
    } else {
      // not included, add it in
      itemSourceIds = [...itemSourceIds, itemSourceId]
    }

    setSelectedItemSourceIds({
      ...selectedItemSourceIds,
      [syncDir]: itemSourceIds
    })
  }

  return [
    selectedItemSourceIds,
    toggleSelectedItemSourceId,
    setSelectedItemSourceIds
  ]
}
